/* import __COLOCATED_TEMPLATE__ from './qa-status-selector.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-experience */

import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import { QaStatusValue, STATUS_ICON_TYPES } from 'embercom/lib/inbox2/qa-status-constants';

interface QaStatusOption {
  id: string | number;
  label: string;
  archived: boolean;
}

interface Args {
  selectedStatus?: string | null;
  onSelect: (status: string | null) => void;
  options: QaStatusOption[];
  dropdownClass?: string;
  showRemoveButton?: boolean;
  onRemove?: (event?: Event) => void;
}

interface Signature {
  Args: Args;
  Element: HTMLElement;
}

export default class QaStatusSelector extends Component<Signature> {
  @service declare intl: IntlService;
  @tracked isHovered = false;

  get label() {
    this.intl.primaryLocale;

    if (!this.args.selectedStatus) {
      return this.intl.t('inbox.search.filters.qa-status.none');
    }

    let option = this.args.options.find((opt) => opt.id === this.args.selectedStatus);
    return option?.label || this.args.selectedStatus;
  }

  get items() {
    this.intl.primaryLocale;

    let activeOptions = this.args.options.filter((option) => !option.archived);
    let pinnedIds = [QaStatusValue.Unreviewed, QaStatusValue.PassedReview];
    let pinnedSet = new Set<string>(pinnedIds);

    let toItem = (option: QaStatusOption) => ({
      text: option.label,
      value: option.id as string | number | null,
      isSelected: option.id === this.args.selectedStatus,
      component: 'inbox2/conversations-table/table-cells/qa-status-select-item',
      componentAttrs: {
        iconType: STATUS_ICON_TYPES[option.id as QaStatusValue] ?? 'dot-neutral',
      },
    });

    let pinnedItems = pinnedIds
      .map((id) => activeOptions.find((o) => o.id === id))
      .filter((o): o is QaStatusOption => o !== undefined)
      .map(toItem);
    let otherItems = activeOptions.filter((o) => !pinnedSet.has(o.id as string)).map(toItem);

    let groups: { items: Record<string, unknown>[] }[] = [
      { items: pinnedItems.length > 0 ? pinnedItems : otherItems },
    ];
    if (pinnedItems.length > 0 && otherItems.length > 0) {
      groups.push({ items: otherItems });
    }

    if (this.args.selectedStatus) {
      groups.push({
        items: [
          {
            text: this.intl.t('inbox.search.filters.qa-status.clear'),
            value: null as string | number | null,
            isSelected: false,
            component: 'inbox2/left-nav/conversation-list/filters-item',
          },
        ],
      });
    }

    return groups;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::Search::QaStatusSelector': typeof QaStatusSelector;
    'inbox2/search/qa-status-selector': typeof QaStatusSelector;
  }
}
